import DrawAction from "../enums/draw-action.js";
import {v4 as uuidv4} from 'uuid';

class ImageEditorUtils {
    static cropElements = ["crop-background", "crop-white-rect", "crop-rect", "middle-"];

    static handleShapeClick(e, drawAction, setCurrentShape, transformerRef, shapes, setStrokeColor, setStrokeSize, setFontSize) {
        if (drawAction) return;

        const currentTarget = e.currentTarget;
        transformerRef?.current?.nodes([currentTarget]);

        if(currentTarget){
            const currentShape = shapes.find(el => el.id === currentTarget.attrs.id);
            setStrokeColor(currentShape.color);
            setStrokeSize(currentShape.strokeSize);
            setFontSize(currentShape.fontSize);
        }

        setCurrentShape(currentTarget.attrs.id)
    }

    static updateShapePosition(id, x, y, setShapes) {
        setShapes(prevShapes =>
            prevShapes.map(shape =>
                shape.id === id ? {...shape, x, y} : shape
            )
        );
    }

    static updateShapesText(id, text, setShapes) {
        setShapes(prev => {
            const newShapes = [...prev];

            const find = newShapes.find(f => f.id === id);
            if (find) find.text = text;

            return newShapes;
        });
    }

    static onStageMouseDown(drawAction, stageRef, transformerRef, currentShape, setCurrentShape, isPaintRef, setShapes, strokeSize, strokeColor, fontSize, isCropping) {
        const stage = stageRef?.current;
        const pos = stage?.getPointerPosition();
        const clickedOn = stage?.getIntersection(pos);
        const selectedNodes = transformerRef.current?.nodes() || [];

        if (isCropping) return;

        if (!drawAction && clickedOn && clickedOn.attrs.name === "image") {
            transformerRef?.current?.nodes([]);
            setCurrentShape(undefined);
            return;
        }

        if ((clickedOn && selectedNodes.includes(clickedOn)) || !drawAction) return;

        isPaintRef.current = true;
        const x = pos?.x || 0;
        const y = pos?.y || 0;
        const id = uuidv4();
        setCurrentShape(id)

        switch(drawAction) {
            case DrawAction.ARROW:{
                setShapes(prev => [...prev, {id, type: DrawAction.ARROW, strokeSize, color: strokeColor, points: [x, y, x, y]}]);
                break;
            }
            case DrawAction.ARROW_RED:{
                setShapes(prev => [...prev, {id, type: DrawAction.ARROW, strokeSize, color: "#FF0000", points: [x, y, x, y]}]);
                break;
            }
            case DrawAction.ARROW_BLUE:{
                setShapes(prev => [...prev, {id, type: DrawAction.ARROW, strokeSize, color: "#007BFF", points: [x, y, x, y]}]);
                break;
            }
            case DrawAction.RECTANGLE:{
                setShapes(prev => [...prev, {id, type: DrawAction.RECTANGLE, strokeSize, color: strokeColor, x, y, height: 1, width: 1 }]);
                break;
            }
            case DrawAction.CIRCLE:{
                setShapes(prev => [...prev, {id, type: DrawAction.CIRCLE, strokeSize, color: strokeColor, x, y, radiusX: 1, radiusY: 1, originX: x, originY: y}]);
                break;
            }
            case DrawAction.TEXT:{
                setShapes(prev => [...prev, {id, type: DrawAction.TEXT, fontSize, isCreating: true, color: strokeColor, x, y, height: 50, width: 100, text: "Clique duas vezes para editar."}]);
                break;
            }
        }
    }

    static onStageMouseMove(drawAction, stageRef, isPaintRef, setShapes, currentShape) {
        if(!isPaintRef.current) return;
        const stage = stageRef?.current;
        const pos = stage?.getPointerPosition();
        const x = pos?.x || 0;
        const y = pos?.y || 0;

        switch(drawAction){
            case DrawAction.ARROW:
            case DrawAction.ARROW_RED:
            case DrawAction.ARROW_BLUE: {
                setShapes((prevShapes) =>
                    prevShapes.map(prevShape =>
                        prevShape.id === currentShape
                            ? {
                                ...prevShape,
                                points: [prevShape?.points[0] || 0, prevShape?.points[1] || 0, x, y]
                            }
                            : prevShape
                    )
                );
                break;
            }
            case DrawAction.RECTANGLE: {
                setShapes((prevShapes) =>
                    prevShapes.map(prevShape =>
                        prevShape.id === currentShape
                            ? {
                                ...prevShape,
                                height: y - prevShape?.y,
                                width: x - prevShape?.x
                            }
                            : prevShape
                    )
                );
                break;
            }
            case DrawAction.CIRCLE: {
                setShapes((prevShapes) =>
                    prevShapes.map(prevShape => {
                        if (prevShape.id !== currentShape) return prevShape;

                        const originX = prevShape.originX;
                        const originY = prevShape.originY;

                        const width = Math.abs(x - originX);
                        const height = Math.abs(y - originY);

                        const centerX = (x + originX) / 2;
                        const centerY = (y + originY) / 2;

                        return {
                            ...prevShape,
                            x: centerX,
                            y: centerY,
                            radiusX: width / 2,
                            radiusY: height / 2
                        };
                    })
                );
                break;
            }
            case DrawAction.TEXT: {
                setShapes((prevShapes) =>
                    prevShapes.map(prevShape =>
                        prevShape.id === currentShape
                            ? {
                                ...prevShape,
                                height: y - prevShape?.y,
                                width: x - prevShape?.x
                            }
                            : prevShape
                    )
                );
                break;
            }
        }
    }

    static onStageMouseUp(isPaintRef, stageRef, currentShape, transformerRef, setShapes, drawAction, setDrawAction, isCropping) {
        const stage = stageRef?.current;

        if (isCropping) return

        isPaintRef.current = false;

        if (drawAction === DrawAction.TEXT) {
            setShapes(prevShapes =>
                prevShapes.map(shape => shape.id === currentShape ? {...shape, isCreating: false} : shape)
            );
        }

        const lastShapeId = currentShape;
        if (stage && lastShapeId) {
            setTimeout(() => {
                const node = stage.findOne(`#${lastShapeId}`);
                if (node) {
                    transformerRef.current?.nodes([node]);
                }
            }, 0);
        }

        setDrawAction(null);
    }

    static handleStrokeSize(strokeSize, setShapes, setStrokeSize, transformerRef) {
        if(transformerRef?.current?.nodes().length > 0){
            setShapes(prev =>
                prev.map(el =>
                    el.id === transformerRef?.current?.nodes()[0].attrs.id
                        ? {...el, strokeSize}
                        : el
                )
            );
        }

        setStrokeSize(strokeSize);
    }

    static handleFontSize(fontSize, setShapes, setFontSize, transformerRef) {
        if(transformerRef?.current?.nodes().length > 0){
            setShapes(prev =>
                 prev.map(el =>
                    el.id === transformerRef?.current?.nodes()[0].attrs.id
                        ? {...el, fontSize}
                        : el
                )
            );
        }

        setFontSize(fontSize);
    }

    static handleStrokeColor(color, setShapes, setStrokeColor, transformerRef) {
        if(transformerRef?.current?.nodes().length > 0){
            setShapes(prev =>
                 prev.map(el =>
                    el.id === transformerRef?.current?.nodes()[0].attrs.id
                        ? {...el, color}
                        : el
                )
            );
        }

        setStrokeColor(color);
    }
}

export default ImageEditorUtils;